<script>
import TalkizyAPI from '@app/services/API/Talkizy';
import talkizyErrorMessage from '@app/utils/talkizyErrors';
import talkizyMixin from '@app/mixins/talkizy';
import TalkizyConnect from '@app/components/TalkizyConnect.vue';
import TalkizyJoinTeam from '@app/components/TalkizyJoinTeam.vue';
import VisioCard from '@app/components/VisioCard.vue';
import { MEETING_LEAD_MS, MEETING_LEAD_MINUTES } from '@shared/utils/meeting';
import {
  DEFAULT_MEETING_TIMEZONE,
  timezoneSelectOptions,
  wallClockToISO,
  wallClockInstantMs,
} from '@shared/utils/meetingTimezone';
import { debounce } from 'lodash';
import moment from 'moment';

const MEETINGS_PER_PAGE = 20;

export default {
  name: 'ClasseVirtuelle',
  components: { TalkizyConnect, TalkizyJoinTeam, VisioCard },
  mixins: [talkizyMixin],
  head: {
    title: 'Classes virtuelles',
  },
  data() {
    return {
      meetings: [],
      pagination: { total: 0, per_page: MEETINGS_PER_PAGE, current_page: 1 },
      page: 1,
      hasFetched: false,
      isMeetingsLoading: false,
      // Membre suspendu côté Talkizy : détecté quand un appel API existant renvoie
      // 403 « member_suspended » (ex. la liste des classes). On bascule alors sur
      // la page d'explication, sans appel dédié ni cache.
      isSuspended: false,
      isCreateModalOpen: false,
      // Popup d'upsell affichée quand un forfait sans accès tente de créer une
      // classe virtuelle (la connexion, elle, reste ouverte à tous les forfaits).
      isPlanLockModalOpen: false,
      isCreating: false,
      // Membres de l'espace éligibles animateur principal (chargés à l'ouverture
      // du modal). Le sélecteur n'apparaît que s'il y en a plusieurs.
      hostOptions: [],
      selectedHostId: null,
      searchFormData: {
        q: '',
        time: '',
        sort_by: 'starts_at',
        order: 'desc',
        mine: false,
      },
      form: this.emptyForm(),
    };
  },
  watch: {
    isTalkizyReady(now) {
      if (now) {
        this.fetchMeetings();
      }
    },
    searchFormData: {
      deep: true,
      handler() {
        this.debouncedSearch();
      },
    },
  },
  computed: {
    isTalkizyReady() {
      // Un membre suspendu n'a plus accès : ni en-tête, ni bouton créer, ni liste
      // (isSuspended est posé quand un appel existant renvoie 403 member_suspended).
      return this.ownerConnected && this.isMember && !this.isSuspended;
    },
    // Jour minimum sélectionnable = aujourd'hui. On borne au début de journée
    // (minuit) car le picker compare des jours à minuit : une borne portant
    // l'heure courante exclurait aujourd'hui. Le délai « au moins N min à
    // l'avance » est vérifié séparément sur l'heure dans submitMeeting.
    minStartDate() {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return d;
    },
    // Créer une classe virtuelle nécessite un abonnement (même règle que le
    // GoProWrapper "PRO"). La connexion Talkizy, elle, reste ouverte à tous.
    canCreateMeeting() {
      return this.$store.getters['auth/hasProPlan']
        || this.$store.getters['auth/hasExpertPlan'];
    },
    searchParamsDirty() {
      const {
        q, time, sort_by: sortBy, order, mine,
      } = this.searchFormData;
      return q !== '' || time !== '' || sortBy !== 'starts_at' || order !== 'desc' || mine;
    },
    debouncedSearch() {
      return debounce(this.search, 400);
    },
    timezoneOptions() {
      return timezoneSelectOptions(this.form.timezone, this.startsAt);
    },
    // Recombine les deux champs (date + heure) en un seul Date dont la roue
    // locale du navigateur porte la date/heure saisie, tel qu'attendu par les
    // helpers de fuseau (wallClockToISO / wallClockInstantMs).
    startsAt() {
      if (!this.form.startDate || !this.form.startTime) return null;
      const [hours, minutes] = this.form.startTime.split(':').map(Number);
      const date = new Date(this.form.startDate);
      date.setHours(hours, minutes, 0, 0);
      return date;
    },
  },
  async mounted() {
    await this.fetchTalkizyStatus();
    if (this.isTalkizyReady) {
      this.fetchMeetings();
    }
  },
  methods: {
    emptyForm() {
      return {
        title: '',
        startDate: null,
        // Prochaine heure ronde à +2 h (ex. à 13h53 → 15:00), comme le champ
        // heure de la création d'un événement du calendrier.
        startTime: moment().startOf('hour').add(2, 'hours').format('HH:mm'),
        duration: 60,
        accessType: 'OPENED',
        timezone: DEFAULT_MEETING_TIMEZONE,
      };
    },

    // Un input[type=time] natif n'ouvre son sélecteur que via l'icône horloge ;
    // un clic sur les chiffres ne fait que sélectionner le segment. showPicker()
    // ouvre le sélecteur quel que soit l'endroit cliqué (no-op si non supporté).
    openTimePicker(event) {
      const input = event.target.tagName === 'INPUT'
        ? event.target
        : event.currentTarget.querySelector('input');

      if (input && typeof input.showPicker === 'function') {
        try {
          input.showPicker();
        } catch (e) {
          // showPicker peut refuser (geste utilisateur requis, iframe…) : la
          // saisie clavier reste possible.
        }
      }
    },

    async fetchMeetings() {
      this.isMeetingsLoading = true;
      try {
        const {
          q, time, sort_by: sortBy, order, mine,
        } = this.searchFormData;
        const params = {
          page: this.page,
          per_page: MEETINGS_PER_PAGE,
          sort_by: sortBy,
          order,
        };
        if (q) params.q = q;
        if (time) params.time = time;
        // Envoyé en 1/absent : en query string, le booléen `true` devient "true",
        // rejeté par la règle Laravel `boolean` (qui accepte 1/0/"1"/"0").
        if (mine) params.mine = 1;

        const res = await TalkizyAPI.listMeetings(params);
        this.meetings = res.data || [];
        this.pagination = res.pagination || { total: 0, per_page: MEETINGS_PER_PAGE, current_page: 1 };
      } catch (e) {
        if (!this.detectSuspended(e)) {
          this.$showMessage.error('Impossible de charger les classes virtuelles.');
        }
      } finally {
        this.isMeetingsLoading = false;
        this.hasFetched = true;
      }
    },

    search() {
      this.page = 1;
      this.fetchMeetings();
    },

    changePage(page) {
      this.page = page;
      this.fetchMeetings();
    },

    toggleOrder() {
      this.searchFormData.order = this.searchFormData.order === 'desc' ? 'asc' : 'desc';
    },

    resetSearchParams() {
      this.searchFormData = {
        q: '',
        time: '',
        sort_by: 'starts_at',
        order: 'desc',
        mine: false,
      };
    },

    openCreateModal() {
      // Forfait sans accès : on affiche l'upsell plutôt que le formulaire.
      if (!this.canCreateMeeting) {
        this.isPlanLockModalOpen = true;
        return;
      }
      this.form = this.emptyForm();
      this.hostOptions = [];
      this.selectedHostId = null;
      this.isCreateModalOpen = true;
      this.fetchHostOptions();
    },

    // Membres éligibles animateur principal. Non bloquant : en cas d'échec, on
    // crée avec l'animateur = utilisateur connecté (comportement historique).
    async fetchHostOptions() {
      try {
        const res = await TalkizyAPI.getMembers();
        this.hostOptions = (res && res.hosts) || [];
        this.selectedHostId = (res && res.current_user_id)
          || (this.hostOptions[0] && this.hostOptions[0].id)
          || null;
      } catch (e) {
        this.hostOptions = [];
        this.selectedHostId = null;
      }
    },

    async submitMeeting() {
      if (!this.form.title || !this.startsAt || !this.form.duration) {
        this.$buefy.toast.open({ message: 'Titre, date, heure et durée sont requis.', type: 'is-warning' });
        return;
      }

      if (wallClockInstantMs(this.startsAt, this.form.timezone) < Date.now() + MEETING_LEAD_MS) {
        this.$buefy.toast.open({
          message: `La classe virtuelle doit être programmée au minimum ${MEETING_LEAD_MINUTES} minutes après l’heure actuelle.`,
          type: 'is-warning',
        });
        return;
      }

      this.isCreating = true;
      try {
        const payload = {
          title: this.form.title,
          starts_at: wallClockToISO(this.startsAt, this.form.timezone),
          duration: Number(this.form.duration),
          timezone: this.form.timezone,
          access_type: this.form.accessType,
        };
        // Animateur principal désigné (seulement si plusieurs membres et choix
        // différent du défaut ; sinon le back retombe sur l'utilisateur connecté).
        if (this.hostOptions.length > 1 && this.selectedHostId) {
          payload.primary_host_user_id = this.selectedHostId;
        }
        const res = await TalkizyAPI.createMeeting(payload);
        this.$buefy.toast.open({ message: 'Classe virtuelle créée.', type: 'is-success' });
        this.isCreateModalOpen = false;

        // Enchaîne directement sur la configuration de la classe créée
        // (participants, émargement, rappels…).
        if (res && res.meeting_id) {
          this.$router.push({
            name: 'classe_virtuelle_meeting_settings',
            params: { meetingId: res.meeting_id },
          });
          return;
        }

        this.page = 1;
        await this.fetchMeetings();
      } catch (e) {
        if (!this.detectSuspended(e)) {
          const message = talkizyErrorMessage(e, 'La création a échoué.');
          this.$showMessage.error(message);
        }
      } finally {
        this.isCreating = false;
      }
    },

    // Un membre suspendu côté Talkizy reçoit 403 « member_suspended » sur les
    // appels existants (liste, création…). On bascule alors sur la page dédiée,
    // sans appel d'API supplémentaire ni cache. Renvoie true si géré.
    detectSuspended(e) {
      if (e?.response?.status === 403 && e?.response?.data?.error === 'member_suspended') {
        this.isSuspended = true;
        return true;
      }
      return false;
    },
  },
};
</script>

<template>
  <div class="cvcomp">
    <header v-if="isTalkizyReady" class="level mb-5">
      <div class="level-left is-block w-80p">
        <h1 class="title mb-0">
          Vos classes virtuelles
        </h1>
      </div>
      <div class="level-right is-hidden-mobile">
        <b-button
          type="is-primary"
          icon-left="plus"
          @click="openCreateModal"
        >
          Créer une classe virtuelle
        </b-button>
      </div>
    </header>

    <div class="cvcomp_body">
      <!-- Membre suspendu côté Talkizy : accès entièrement bloqué. On n'affiche
           qu'un message l'invitant à contacter le propriétaire de son espace. -->
      <div v-if="isSuspended" class="box has-text-centered mx-auto mw-520 py-6 px-5">
        <b-icon icon="lock" size="is-large" type="is-warning" class="mb-4" />
        <h2 class="title is-4">
          Votre accès aux classes virtuelles est suspendu
        </h2>
        <p class="content">
          Votre compte Talkizy a été suspendu par le propriétaire de votre espace.
          Vous ne pouvez plus créer ni consulter de classes virtuelles.
        </p>
        <p class="has-text-grey">
          Pour rétablir votre accès, rapprochez-vous du propriétaire de votre espace.
        </p>
      </div>

      <!-- ADMIN pas encore membre, espace déjà connecté : invitation à rejoindre.
           Espace non connecté : on retombe sur TalkizyConnect ci-dessous, en lecture seule. -->
      <TalkizyJoinTeam
        v-else-if="isAdminRole && !isMember && ownerConnected"
        :can-join="canJoin"
        :is-loading="isLoading"
        @join="openJoin"
      />

      <!-- Espace non connecté à Talkizy : flux SSO pour le owner,
           même page en lecture seule pour l'admin (boutons désactivés). -->
      <TalkizyConnect
        v-else-if="!ownerConnected"
        :is-loading="isLoading"
        :is-login-loading="isLoginLoading"
        :disabled="isAdminRole"
        @register="openRegistration"
        @login="openLogin"
      />

      <div v-else class="cvcomp_connected">
        <div class="cvcomp_meetings">
          <b-skeleton v-if="!hasFetched" height="120" :count="2" :animated="true" />

          <EmptyState
            v-else-if="pagination.total === 0 && !searchParamsDirty"
            title="Vous n'avez pas encore de classes virtuelles"
            text="Cliquez sur le bouton ci-dessous pour en créer une dès maintenant."
            cta-text="Créer une classe virtuelle"
            :cta-func="openCreateModal"
          />

          <template v-else>
            <h3 class="mb-8 is-size-8">
              Retrouvez ici toutes vos classes virtuelles.<br/>
              Filtrez par nom ou par statut (à venir / passées), et triez la liste
              pour retrouver rapidement une classe virtuelle.
            </h3>

            <form class="mb-5" @submit.prevent>
              <div class="columns" style="margin-bottom: 0;">
                <div class="column">
                  <b-field label="Filtrer par">
                    <b-input
                      v-model="searchFormData.q"
                      placeholder="Filtrer par nom"
                      :loading="isMeetingsLoading"
                    />
                  </b-field>
                </div>
                <div class="column is-narrow">
                  <b-field label="Statut">
                    <b-select v-model="searchFormData.time">
                      <option value="">
                        Toutes
                      </option>
                      <option value="upcoming">
                        À venir
                      </option>
                      <option value="past">
                        Passées
                      </option>
                    </b-select>
                  </b-field>
                </div>
                <div class="column is-narrow">
                  <b-field label="Créateur">
                    <b-select v-model="searchFormData.mine">
                      <option :value="false">
                        Toutes
                      </option>
                      <option :value="true">
                        Créées par moi
                      </option>
                    </b-select>
                  </b-field>
                </div>
                <div class="column is-narrow">
                  <b-field label="Trier par">
                    <b-select v-model="searchFormData.sort_by">
                      <option value="starts_at">
                        Date
                      </option>
                      <option value="title">
                        Nom
                      </option>
                    </b-select>
                    <p class="control">
                      <b-tooltip :label="searchFormData.order === 'asc' ? 'croissant' : 'décroissant'">
                        <b-button
                          :icon-left="searchFormData.order === 'asc' ? 'arrow-up' : 'arrow-down'"
                          @click="toggleOrder"
                        />
                      </b-tooltip>
                    </p>
                  </b-field>
                </div>
              </div>
            </form>

            <div v-if="searchParamsDirty" class="has-text-right mb-6">
              <a
                href="#"
                class="is-size-8 is-inline-block"
                style="cursor:pointer;"
                @click.prevent="resetSearchParams"
              >
                <strong>Réinitialiser les paramètres de recherche</strong>
              </a>
            </div>

            <template v-if="meetings.length">
              <VisioCard
                v-for="m in meetings"
                :key="m.meeting_id"
                :meeting="m"
                class="mb-4"
                @deleted="fetchMeetings"
              />

              <b-pagination
                v-if="pagination.total > pagination.per_page"
                :current="page"
                :total="pagination.total"
                :per-page="pagination.per_page"
                order="is-right"
                class="mt-5"
                aria-next-label="Page suivante"
                aria-previous-label="Page précédente"
                aria-page-label="Page"
                aria-current-label="Page courante"
                @change="changePage"
              />
            </template>

            <EmptyState
              v-else
              title="Aucun résultat trouvé"
              text="Aucune classe virtuelle ne correspond à votre recherche."
              cta-text="Réinitialiser les paramètres de recherche"
              :cta-func="resetSearchParams"
            />
          </template>
        </div>

        <b-modal v-model="isCreateModalOpen" has-modal-card trap-focus :destroy-on-hide="false">
          <div class="modal-card mw-640">
            <header class="modal-card-head">
              <p class="modal-card-title">
                Créer une classe virtuelle
              </p>
            </header>
            <section class="modal-card-body">
              <b-field label="Titre">
                <b-input v-model="form.title" placeholder="Ex. Cours du lundi" maxlength="255" required />
              </b-field>

              <div class="columns">
                <div class="column is-4">
                  <b-field label="Date">
                    <b-datepicker
                      v-model="form.startDate"
                      placeholder="Sélectionner..."
                      icon="calendar"
                      append-to-body
                      :first-day-of-week="1"
                      :min-date="minStartDate"
                    />
                  </b-field>
                </div>
                <div class="column is-3">
                  <b-field label="Heure de début" label-for="meeting_start_time">
                    <b-input
                      id="meeting_start_time"
                      v-model="form.startTime"
                      type="time"
                      icon="clock"
                      expanded
                      @click.native="openTimePicker"
                    />
                  </b-field>
                </div>
                <div class="column is-5">
                  <b-field label="Fuseau horaire">
                    <b-select v-model="form.timezone" expanded>
                      <option
                        v-for="tz in timezoneOptions"
                        :key="tz.value"
                        :value="tz.value"
                      >
                        {{ tz.label }}
                      </option>
                    </b-select>
                  </b-field>
                </div>
              </div>

              <div class="columns">
                <div class="column is-7">
                  <b-field label="Type d'accès">
                    <b-select v-model="form.accessType" expanded>
                      <option value="OPENED">
                        Ouvert (sans salle d'attente)
                      </option>
                      <option value="CLOSED">
                        Fermé (avec salle d'attente)
                      </option>
                    </b-select>
                  </b-field>
                </div>
                <div class="column is-5">
                  <b-field label="Durée prévisionnelle (en min)">
                    <b-input v-model="form.duration" type="number" min="1" max="480" />
                  </b-field>
                </div>
              </div>

              <!-- Animateur principal : proposé uniquement si plusieurs membres
                   de l'espace ont fait le SSO (défaut = utilisateur connecté). -->
              <b-field
                v-if="hostOptions.length > 1"
                label="Animateur principal"
              >
                <b-select v-model="selectedHostId" expanded>
                  <option
                    v-for="host in hostOptions"
                    :key="host.id"
                    :value="host.id"
                  >
                    {{ host.name }} ({{ host.email }})
                  </option>
                </b-select>
              </b-field>
              <p class="notification">
                Cette classe virtuelle sera automatiquement ajoutée à votre calendrier Teachizy
                et à celui des apprenants que vous inviterez.
              </p>
            </section>
            <footer class="modal-card-foot is-justify-content-flex-end">
              <b-button @click="isCreateModalOpen = false">
                Annuler
              </b-button>
              <b-button type="is-primary" :loading="isCreating" @click="submitMeeting">
                Créer
              </b-button>
            </footer>
          </div>
        </b-modal>
      </div>
    </div>

    <!-- Forfait sans accès : au clic sur « Créer une classe virtuelle », on
         affiche le message d'upsell (GoProWrapper) au lieu du formulaire. -->
    <b-modal v-model="isPlanLockModalOpen" has-modal-card trap-focus>
      <div class="modal-card mw-640">
        <header class="modal-card-head">
          <p class="modal-card-title">
            Créer une classe virtuelle
          </p>
        </header>
        <section class="modal-card-body">
          <GoProWrapper
            plan-lock="PRO"
            :has-sep="false"
            @cta-click="isPlanLockModalOpen = false"
          />
        </section>
      </div>
    </b-modal>
  </div>
</template>

<style lang="scss" scoped>
.cvcomp {
  &_meetings {
    width: 100%;
  }

  // Paginateur : numéros de page à gauche, flèches < > regroupées à leur droite.
  ::v-deep .pagination {
    justify-content: flex-end;

    .pagination-list {
      order: 1;
      flex-grow: 0;
      flex-basis: auto;
      margin-right: 0.25rem;
    }

    .pagination-previous {
      order: 2;
    }

    .pagination-next {
      order: 3;
    }
  }
}

// Le clic ouvre le sélecteur natif (showPicker) : le curseur main signale que
// tout le champ heure est cliquable, comme le champ heure du calendrier.
::v-deep input[type='time'] {
  cursor: pointer;
}
</style>

<style lang="scss">
// `append-to-body` téléporte le popup du datepicker sur <body>, hors de la
// portée des styles scoped. Quand la fenêtre est trop courte, le popup dépasse
// le viewport et la modale ouverte bloque le scroll de page : on plafonne donc
// sa hauteur au viewport avec un scroll interne pour garder le calendrier,
// l'heure et le bouton de validation atteignables.
.dropdown-content:has(.datepicker-header) {
  max-height: calc(100vh - 20px);
  overflow-y: auto;
}
</style>
